/**
 * MobileFeeEditDialog Component
 * 
 * Mobile-friendly dialog for editing transfer fee or expense fee transactions.
 * Only allows editing fee amount and fee account.
 * Shows related transfer or expense information (read-only) with transaction ID and copy.
 */

import React, { FormEvent, useState, useRef, useCallback, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Stack,
  IconButton,
  InputAdornment,
  Paper,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Account, Currency } from "../../../../types";
import { TransactionWithDate } from "../../../../utils/transactionProcessing";
import { formatCurrency, formatDateLocal, formatTimeLocal, getTransactionFriendlyId, copyToClipboard } from "../../../../utils/transactionUtils";
import { getCurrencyDecimalPlaces } from "../../../../utils/currencyDefaults";
import CopyFeedbackTooltip from "../../../../components/CopyFeedbackTooltip";
import { FeeEditFormData } from "../../components/FeeEditDialog";
import MobileCalculator from "./MobileCalculator";
import MobileBottomPanel from "./MobileBottomPanel";
import { convertFormattedAmountToRawValue } from "../../../../utils/currencyDefaults";

interface MobileFeeEditDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  
  // Form data
  formData: FeeEditFormData;
  setFormData: React.Dispatch<React.SetStateAction<FeeEditFormData>>;
  
  // Fee transaction being edited
  feeTransaction: TransactionWithDate;
  
  // Related transfer or expense transaction (read-only info)
  transferTransaction: TransactionWithDate | null;
  expenseTransaction?: TransactionWithDate | null;
  
  // For showing transaction ID on related cards
  transactionIdPrefix?: string;
  
  // Data
  accounts: Account[];
  currencies: Currency[];
  defaultCurrency: string;
  
  // State
  isSubmitting?: boolean;
  
  // Validation
  isValid?: boolean;
  /** Disable Update button when user has not made any changes (default true when not provided) */
  hasFeeChanges?: boolean;
  
  // Navigation
  workspaceSlug?: string;
}

type FocusedField = "feeAmount" | "feeAccount" | null;

export default function MobileFeeEditDialog({
  open,
  onClose,
  onSubmit,
  formData,
  setFormData,
  feeTransaction,
  transferTransaction,
  expenseTransaction = null,
  transactionIdPrefix = "",
  accounts,
  currencies,
  defaultCurrency,
  isSubmitting = false,
  isValid = true,
  hasFeeChanges = true,
  workspaceSlug,
}: MobileFeeEditDialogProps): React.ReactElement {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [lastCopiedKey, setLastCopiedKey] = useState<string | null>(null);
  const copyFeedbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showCopiedFeedback = useCallback((key: string) => {
    if (copyFeedbackTimeoutRef.current) clearTimeout(copyFeedbackTimeoutRef.current);
    setLastCopiedKey(key);
    copyFeedbackTimeoutRef.current = setTimeout(() => {
      setLastCopiedKey(null);
      copyFeedbackTimeoutRef.current = null;
    }, 2000);
  }, []);
  useEffect(() => () => { if (copyFeedbackTimeoutRef.current) clearTimeout(copyFeedbackTimeoutRef.current); }, []);

  // Mobile interaction state - MUST be declared before any early returns
  const [focusedField, setFocusedField] = useState<FocusedField>(null);
  const [calculatorExpression, setCalculatorExpression] = useState<string>("");
  const [calculatorResetKey, setCalculatorResetKey] = useState<number>(0);
  const amountInputRef = useRef<HTMLInputElement>(null);
  const bottomPanelRef = useRef<HTMLDivElement>(null);
  const hasEvaluatedRef = useRef<boolean>(false);

  const feeAccount = formData.feeAccountId
    ? accounts.find((acc) => acc.id === formData.feeAccountId && !acc.isArchived)
    : null;

  // Handle field focus
  const handleFieldFocus = useCallback((field: FocusedField) => {
    setFocusedField(field);
    if (field === "feeAmount") {
      // Initialize calculator with current amount
      const currentAmount = formData.feeAmount || "0";
      setCalculatorExpression(currentAmount === "" ? "0" : currentAmount);
      hasEvaluatedRef.current = false;
    }
  }, [formData.feeAmount]);

  // Handle calculator check
  const handleCalculatorCheck = useCallback((value: string) => {
    if (focusedField === "feeAmount") {
      // Format fee amount based on fee account currency decimal places
      const formattedAmount = value === "0" ? "0" : value;
      setFormData({ ...formData, feeAmount: formattedAmount });
      setFocusedField(null);
      hasEvaluatedRef.current = false;
    }
  }, [focusedField, formData, setFormData]);

  // Handle account selection from bottom panel
  const handleAccountSelect = useCallback((accountId: string) => {
    setFormData({ ...formData, feeAccountId: accountId });
    setFocusedField(null);
  }, [formData, setFormData]);

  // Handle dialog close - reset focus state
  const handleClose = useCallback(() => {
    setFocusedField(null);
    setCalculatorExpression("");
    onClose();
  }, [onClose]);

  // Get transfer accounts
  const fromAccount = transferTransaction?.fromAccountId
    ? accounts.find((acc) => acc.id === transferTransaction.fromAccountId && !acc.isArchived)
    : null;
  const toAccount = transferTransaction?.toAccountId
    ? accounts.find((acc) => acc.id === transferTransaction.toAccountId && !acc.isArchived)
    : null;

  // Format transfer date and time
  const transferDate = transferTransaction?.date
    ? formatDateLocal(transferTransaction.date)
    : "";
  const transferTime = transferTransaction?.date
    ? formatTimeLocal(transferTransaction.date)
    : "";
  const hasTransferTime = transferTime && transferTime !== "00:00";

  // Don't render if not open - but hooks must be called first
  if (!open) return <></>;

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.default",
        overflow: "hidden",
        width: "100%",
        maxWidth: "100vw",
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1300,
      }}
    >
      {/* Header with back button */}
      <Paper
        elevation={0}
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          p: 1.6,
          display: "flex",
          alignItems: "center",
          gap: 1,
          position: "sticky",
          top: 0,
          zIndex: 1500,
          bgcolor: "background.paper",
        }}
      >
        <IconButton 
          onClick={handleClose}
          sx={{
            p: 0.75,
            color: "text.primary",
            touchAction: "manipulation",
            WebkitTapHighlightColor: "transparent",
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
        >
          <ArrowBackIcon sx={{ fontSize: "1.25rem" }} />
        </IconButton>
        <Typography 
          variant="subtitle1" 
          sx={{ 
            flex: 1, 
            color: "text.primary",
            fontWeight: 500,
            fontSize: "1rem"
          }}
        >
          Edit Expense (Fee)
        </Typography>
      </Paper>

      {/* Form Content - Scrollable */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          WebkitOverflowScrolling: "touch",
          px: 2,
          pb: focusedField ? (focusedField === "feeAmount" ? "380px" : "300px") : 2,
          width: "100%",
          maxWidth: "100%",
        }}
      >
        <form onSubmit={onSubmit} id="fee-edit-form">
          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* Related Transfer Information (Read-only) */}
            {transferTransaction && (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: "action.hover",
                  borderRadius: 1,
                  border: 1,
                  borderColor: "divider",
                }}
              >
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
                  Related Transfer
                </Typography>
                <Stack spacing={1}>
                  {/* From Account */}
                  {fromAccount && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        From Account
                      </Typography>
                      <Typography variant="body2">
                        {fromAccount.accountName} ({formatCurrency(fromAccount.amount || 0, fromAccount.currency, getCurrencyDecimalPlaces(fromAccount.currency, currencies, defaultCurrency))} {fromAccount.currency})
                      </Typography>
                    </Box>
                  )}

                  {/* To Account */}
                  {toAccount && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        To Account
                      </Typography>
                      <Typography variant="body2">
                        {toAccount.accountName} ({formatCurrency(toAccount.amount || 0, toAccount.currency, getCurrencyDecimalPlaces(toAccount.currency, currencies, defaultCurrency))} {toAccount.currency})
                      </Typography>
                    </Box>
                  )}

                  {/* Transfer Amount */}
                  {transferTransaction.amount !== undefined && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Transfer Amount
                      </Typography>
                      <Typography variant="body2">
                        {formatCurrency(transferTransaction.amount, transferTransaction.currency || defaultCurrency, getCurrencyDecimalPlaces(transferTransaction.currency || defaultCurrency, currencies, defaultCurrency))}
                      </Typography>
                    </Box>
                  )}

                  {/* Date */}
                  {transferDate && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Date
                      </Typography>
                      <Typography variant="body2">{transferDate}</Typography>
                    </Box>
                  )}

                  {/* Time (if exists) */}
                  {hasTransferTime && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Time
                      </Typography>
                      <Typography variant="body2">{transferTime}</Typography>
                    </Box>
                  )}

                  {/* Transaction ID with copy */}
                  {transferTransaction && (getTransactionFriendlyId(transactionIdPrefix, transferTransaction.friendlyIdSequence) || transferTransaction.id) && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        ID
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: "0.8125rem", minWidth: 0 }}>
                        {getTransactionFriendlyId(transactionIdPrefix, transferTransaction.friendlyIdSequence) ?? transferTransaction.id}
                      </Typography>
                      <CopyFeedbackTooltip copyLabel="Copy ID" feedbackKey={`mobile-fee-dialog-transfer-id-${transferTransaction.id}`} lastCopiedKey={lastCopiedKey} isMobile={true}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            const id = getTransactionFriendlyId(transactionIdPrefix, transferTransaction.friendlyIdSequence) ?? transferTransaction.id;
                            copyToClipboard(id).then((ok) => ok && showCopiedFeedback(`mobile-fee-dialog-transfer-id-${transferTransaction.id}`));
                          }}
                          sx={{ p: 0.25 }}
                          aria-label="Copy transaction ID"
                        >
                          <ContentCopyIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </CopyFeedbackTooltip>
                    </Box>
                  )}
                </Stack>
              </Paper>
            )}

            {/* Related Expense (read-only) - for expense fee */}
            {expenseTransaction && (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: "action.hover",
                  borderRadius: 1,
                  border: 1,
                  borderColor: "divider",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap", mb: 0.5 }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    Related Expense
                  </Typography>
                  {(getTransactionFriendlyId(transactionIdPrefix, expenseTransaction.friendlyIdSequence) || expenseTransaction.id) && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8125rem" }}>
                        ID: {getTransactionFriendlyId(transactionIdPrefix, expenseTransaction.friendlyIdSequence) ?? expenseTransaction.id}
                      </Typography>
                      <CopyFeedbackTooltip copyLabel="Copy ID" feedbackKey={`mobile-fee-dialog-expense-id-${expenseTransaction.id}`} lastCopiedKey={lastCopiedKey} isMobile={true}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            const id = getTransactionFriendlyId(transactionIdPrefix, expenseTransaction.friendlyIdSequence) ?? expenseTransaction.id;
                            copyToClipboard(id).then((ok) => ok && showCopiedFeedback(`mobile-fee-dialog-expense-id-${expenseTransaction.id}`));
                          }}
                          sx={{ p: 0.25 }}
                          aria-label="Copy transaction ID"
                        >
                          <ContentCopyIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </CopyFeedbackTooltip>
                    </Box>
                  )}
                </Box>
                <Stack spacing={1}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Category
                    </Typography>
                    <Typography variant="body2">{expenseTransaction.category ?? expenseTransaction.title ?? ""}</Typography>
                  </Box>
                  {expenseTransaction.amount !== undefined && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Amount
                      </Typography>
                      <Typography variant="body2">
                        {formatCurrency(expenseTransaction.amount, expenseTransaction.currency || defaultCurrency, getCurrencyDecimalPlaces(expenseTransaction.currency || defaultCurrency, currencies, defaultCurrency))}
                      </Typography>
                    </Box>
                  )}
                  {expenseTransaction.date && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Date
                      </Typography>
                      <Typography variant="body2">{formatDateLocal(expenseTransaction.date)}</Typography>
                    </Box>
                  )}
                </Stack>
              </Paper>
            )}

            <Divider />

            {/* Editable Fields */}
            <Typography variant="subtitle2" fontWeight="bold">
              Fee Details
            </Typography>

            {/* Fee Amount */}
            <TextField
              required
              label="Fee Amount"
              type="text"
              inputRef={amountInputRef}
              value={focusedField === "feeAmount" && calculatorExpression !== null && calculatorExpression !== undefined && calculatorExpression.trim() !== ""
                ? calculatorExpression
                : formData.feeAmount || ""}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                const value = formData.feeAmount || "0";
                if (focusedField === "feeAmount") {
                  // Calculator already open, don't do anything
                  return;
                } else {
                  // Open calculator
                  setCalculatorExpression(value === "" ? "0" : value);
                  handleFieldFocus("feeAmount");
                }
              }}
              onFocus={(e) => {
                e.preventDefault();
                e.stopPropagation();
                (e.target as HTMLInputElement).blur();
                const value = formData.feeAmount || "0";
                if (focusedField === "feeAmount") {
                  // Calculator already open, don't do anything
                  return;
                } else {
                  // Open calculator
                  setCalculatorExpression(value === "" ? "0" : value);
                  handleFieldFocus("feeAmount");
                }
              }}
              fullWidth
              placeholder="0.00"
              tabIndex={-1}
              inputProps={{
                inputMode: "none", // Prevent keyboard on mobile
              }}
              InputProps={{
                endAdornment: feeAccount ? (
                  <InputAdornment position="end">
                    <Typography variant="body2" color="text.secondary">
                      {feeAccount.currency}
                    </Typography>
                  </InputAdornment>
                ) : undefined,
                readOnly: true, // Always read-only to prevent keyboard
              }}
              sx={{
                "& .MuiInputBase-input": {
                  cursor: "pointer",
                },
              }}
            />

            {/* Fee Account */}
            <TextField
              required
              label="Fee Account"
              value={feeAccount ? `${feeAccount.accountName} (${formatCurrency(feeAccount.amount || 0, feeAccount.currency, getCurrencyDecimalPlaces(feeAccount.currency, currencies, defaultCurrency))} ${feeAccount.currency})` : ""}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleFieldFocus("feeAccount");
              }}
              onFocus={(e) => {
                e.preventDefault();
                e.stopPropagation();
                (e.target as HTMLInputElement).blur();
                handleFieldFocus("feeAccount");
              }}
              fullWidth
              tabIndex={-1}
              inputProps={{
                inputMode: "none", // Prevent keyboard on mobile
              }}
              InputProps={{
                readOnly: true,
              }}
              sx={{
                "& .MuiInputBase-input": {
                  cursor: "pointer",
                },
              }}
            />
          </Stack>
        </form>
      </Box>

      {/* Submit Button - Fixed at bottom */}
      <Paper
        elevation={0}
        sx={{
          borderTop: 1,
          borderColor: "divider",
          px: 2,
          py: 2,
          position: "sticky",
          bottom: 0,
          zIndex: 1400,
          bgcolor: "background.paper",
          visibility: focusedField ? "hidden" : "visible",
          opacity: focusedField ? 0 : 1,
          transition: "opacity 0.2s ease, visibility 0.2s ease",
          pointerEvents: focusedField ? "none" : "auto",
        }}
      >
        <Stack direction="row" spacing={2}>
          <Button
            onClick={handleClose}
            variant="outlined"
            fullWidth
            disabled={isSubmitting}
            sx={{
              height: "56px",
              minHeight: "56px",
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="fee-edit-form"
            variant="contained"
            fullWidth
            disabled={isSubmitting || !isValid || !hasFeeChanges}
            sx={{
              height: "56px",
              minHeight: "56px",
              touchAction: "manipulation",
              WebkitTapHighlightColor: "transparent",
            }}
          >
            {isSubmitting ? "Updating..." : "Update Fee"}
          </Button>
        </Stack>
      </Paper>

      {/* Calculator */}
      {isMobile && (
        <MobileCalculator
          key={`calc-${calculatorResetKey}`}
          open={focusedField === "feeAmount"}
          initialValue={
            focusedField === "feeAmount"
              ? calculatorExpression !== null && calculatorExpression !== undefined && calculatorExpression.trim() !== ""
                ? calculatorExpression
                : formData.feeAmount && formData.feeAmount !== "0"
                  ? convertFormattedAmountToRawValue(formData.feeAmount)
                  : "0"
              : "0"
          }
          onValueChange={(value) => {
            setCalculatorExpression(value);
            // Update form data immediately when user types
            const cleanValue = value === "0" ? "0" : value;
            setFormData({ ...formData, feeAmount: cleanValue });
          }}
          onCheck={handleCalculatorCheck}
          onClose={() => {
            setFocusedField(null);
          }}
        />
      )}

      {/* Bottom Panel for Account Selection */}
      {isMobile && (
        <MobileBottomPanel
          open={focusedField === "feeAccount"}
          focusedField={focusedField === "feeAccount" ? "feeAccount" : null}
          accounts={accounts.filter((acc) => !acc.isArchived)}
          selectedFeeAccountId={formData.feeAccountId}
          onAccountSelect={handleAccountSelect}
          currencies={currencies}
          defaultCurrency={defaultCurrency}
          onCurrencySelect={() => {}} // Not used for fee account selection
          categories={[]}
          categoryGroups={[]}
          onCategorySelect={() => {}} // Not used for fee account selection
          formatCurrency={(amount, currency) => formatCurrency(amount, currency, getCurrencyDecimalPlaces(currency, currencies, defaultCurrency))}
        />
      )}
    </Box>
  );
}
